import React, { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import {
  listUsers,
  inviteUser,
  generateInviteLink,
  setUserRole,
  setUserActive,
} from '../../services/adminApi';
import '../../styles/usersPanel.scss';

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return '—';
  }
};

const UsersPanel = () => {
  const { session, user } = useContext(AuthContext);
  const token = session?.access_token;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');
  const [inviteErr, setInviteErr] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');

  const refresh = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await listUsers(token);
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la lista de usuarios.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleToggleRole = async (target) => {
    setBusyId(target.id);
    setError('');
    try {
      const nextRole = target.role === 'admin' ? 'user' : 'admin';
      await setUserRole(token, target.id, nextRole);
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (target) => {
    setBusyId(target.id);
    setError('');
    try {
      await setUserActive(token, target.id, target.disabled); // disabled → reactivate
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const resetInviteFeedback = () => {
    setInviteMsg('');
    setInviteErr('');
    setGeneratedLink('');
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    resetInviteFeedback();
    setInviteBusy(true);
    try {
      await inviteUser(token, inviteEmail.trim(), window.location.origin);
      setInviteMsg(`Invitación enviada a ${inviteEmail.trim()}.`);
      setInviteEmail('');
      await refresh();
    } catch (err) {
      setInviteErr(err.message);
    } finally {
      setInviteBusy(false);
    }
  };

  const handleGenerateLink = async () => {
    resetInviteFeedback();
    setInviteBusy(true);
    try {
      const data = await generateInviteLink(token, inviteEmail.trim(), window.location.origin);
      setGeneratedLink(data.link);
      await refresh();
    } catch (err) {
      setInviteErr(err.message);
    } finally {
      setInviteBusy(false);
    }
  };

  const copyLink = () => {
    if (generatedLink) navigator.clipboard?.writeText(generatedLink);
  };

  return (
    <div className="users-panel">
      <h1>Users</h1>

      <section className="users-invite">
        <h2>Invitar usuario</h2>
        <form className="users-invite-form" onSubmit={handleSendInvite}>
          <input
            type="email"
            placeholder="nombre@mangonelawfirmllc.com"
            value={inviteEmail}
            onChange={(e) => {
              setInviteEmail(e.target.value);
              resetInviteFeedback();
            }}
            required
          />
          <button type="submit" className="primary" disabled={inviteBusy || !inviteEmail}>
            {inviteBusy ? '...' : 'Enviar invitación'}
          </button>
          <button
            type="button"
            className="secondary"
            onClick={handleGenerateLink}
            disabled={inviteBusy || !inviteEmail}
          >
            Generar link
          </button>
        </form>
        {inviteMsg && <p className="users-notice">{inviteMsg}</p>}
        {inviteErr && <p className="users-error">{inviteErr}</p>}
        {generatedLink && (
          <div className="users-link-box">
            <input type="text" readOnly value={generatedLink} onFocus={(e) => e.target.select()} />
            <button type="button" onClick={copyLink}>Copiar</button>
          </div>
        )}
      </section>

      <section className="users-list">
        <div className="users-list-header">
          <h2>Usuarios</h2>
          <button type="button" onClick={refresh} disabled={loading}>
            {loading ? 'Cargando...' : 'Refrescar'}
          </button>
        </div>

        {error && <p className="users-error">{error}</p>}

        <table className="users-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Rol</th>
              <th>Estado</th>
              <th>Última sesión</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === user?.id;
              return (
                <tr key={u.id} className={u.disabled ? 'is-disabled' : ''}>
                  <td>{u.email}{isSelf && <span className="tag-self"> (tú)</span>}</td>
                  <td>
                    <span className={`role-badge ${u.role}`}>{u.role}</span>
                  </td>
                  <td>
                    <span className={`status-badge ${u.disabled ? 'off' : 'on'}`}>
                      {u.disabled ? 'Desactivado' : 'Activo'}
                    </span>
                  </td>
                  <td>{formatDate(u.last_sign_in_at)}</td>
                  <td className="actions">
                    <button
                      type="button"
                      onClick={() => handleToggleRole(u)}
                      disabled={isSelf || busyId === u.id}
                      title={isSelf ? 'No puedes cambiar tu propio rol' : ''}
                    >
                      {u.role === 'admin' ? 'Quitar admin' : 'Hacer admin'}
                    </button>
                    <button
                      type="button"
                      className={u.disabled ? 'reactivate' : 'deactivate'}
                      onClick={() => handleToggleActive(u)}
                      disabled={isSelf || busyId === u.id}
                      title={isSelf ? 'No puedes desactivar tu cuenta' : ''}
                    >
                      {u.disabled ? 'Reactivar' : 'Desactivar'}
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">No hay usuarios.</td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default UsersPanel;
